import { useEffect, useMemo, useRef, useState } from "react";
import {
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Loader2,
  Image,
  Video,
  X,
  ChevronDown,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBusiness } from "@/hooks/use-business";
import { useServices } from "@/hooks/use-services";
import { useServiceCategories } from "@/hooks/use-service-categories";
import { useOrderMenus } from "@/hooks/use-order-menus";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// NOTE: This is intentionally a “copy of Services page” but with food wording.
// We continue using the existing `services` + `service_categories` tables so we don't reinvent the wheel.

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatPrice = (amount: number | string | null | undefined, currency: string = "GHS"): string => {
  if (!amount) return `${getCurrencySymbol(currency)}0.00`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return `${getCurrencySymbol(currency)}${numAmount.toFixed(2)}`;
};

// Light-weight preset catalog for food vendors
interface PresetItem {
  name: string;
  description: string;
  price: number;
}

interface PresetCategory {
  name: string;
  items: PresetItem[];
}

const menuCatalog: PresetCategory[] = [
  {
    name: "Rice & Bowls",
    items: [
      { name: "Jollof Rice", description: "Smoky West African jollof rice with protein option.", price: 35 },
      { name: "Fried Rice", description: "Vegetable fried rice, served hot.", price: 35 },
      { name: "Waakye", description: "Waakye with sides (choose extras).", price: 30 },
    ],
  },
  {
    name: "Soups & Swallow",
    items: [
      { name: "Light Soup + Fufu", description: "Spiced light soup with fufu.", price: 45 },
      { name: "Groundnut Soup + Rice Balls", description: "Rich groundnut soup served with omo tuo.", price: 45 },
    ],
  },
  {
    name: "Snacks",
    items: [
      { name: "Kelewele", description: "Spicy fried plantain cubes.", price: 15 },
      { name: "Spring Rolls", description: "Crispy vegetable spring rolls.", price: 18 },
    ],
  },
  {
    name: "Drinks",
    items: [
      { name: "Sobolo", description: "Chilled hibiscus drink.", price: 10 },
      { name: "Fresh Juice", description: "Seasonal fresh juice (ask availability).", price: 12 },
    ],
  },
];

export default function MenuItemsManager() {
  const { business, loading: businessLoading } = useBusiness();
  const { services, loading: servicesLoading, refetch } = useServices();
  const {
    categories,
    loading: categoriesLoading,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useServiceCategories();
  const { menus: orderMenus, loading: menusLoading, createMenu, updateMenu, setDefaultMenu, deleteMenu, refetch: refetchMenus } = useOrderMenus();
  const { toast } = useToast();

  const ordersMultipleMenusEnabled = (business as any)?.orders_multiple_menus_enabled === true;

  const currency = business?.currency || "GHS";

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all"); // all | none | categoryId
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

  const [formName, setFormName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formCategoryId, setFormCategoryId] = useState<string>("none");
  const [formMenuId, setFormMenuId] = useState<string>("");

  // Add-ons (reuses service_addons table)
  const [formAddons, setFormAddons] = useState<
    Array<{ id?: string; name: string; price: string; maxQuantity: string }>
  >([]);
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Category form state (manager dialog)
  const [catEditingId, setCatEditingId] = useState<string | null>(null);
  const [catName, setCatName] = useState("");
  const [catPosition, setCatPosition] = useState("1");
  const [catActive, setCatActive] = useState(true);
  const [catAllowMultiple, setCatAllowMultiple] = useState(false);
  const [catMenuId, setCatMenuId] = useState<string>("");
  const [selectedMenuId, setSelectedMenuId] = useState<string>("");
  const [isMenusOpen, setIsMenusOpen] = useState(false);
  const [menuFormName, setMenuFormName] = useState("");
  const [menuEditingId, setMenuEditingId] = useState<string | null>(null);

  // Media
  const [media, setMedia] = useState<Array<{ url: string; type: "image" | "video"; path?: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Presets UI
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [selectedPresetCategory, setSelectedPresetCategory] = useState(menuCatalog[0]?.name || "");
  const selectedPresetItems = useMemo(
    () => menuCatalog.find((c) => c.name === selectedPresetCategory)?.items || [],
    [selectedPresetCategory],
  );

  const isLoading = businessLoading || servicesLoading;

  const categoryById = useMemo(() => {
    const m = new Map<string, any>();
    (categories || []).forEach((c: any) => {
      if (c?.id) m.set(String(c.id), c);
    });
    return m;
  }, [categories]);

  const categoriesForSelectedMenu = useMemo(() => {
    if (!ordersMultipleMenusEnabled || !selectedMenuId) return categories || [];
    return (categories || []).filter((c: any) => String(c.menu_id || "") === String(selectedMenuId));
  }, [categories, ordersMultipleMenusEnabled, selectedMenuId]);

  const categoriesForForm = useMemo(() => {
    if (!ordersMultipleMenusEnabled || !formMenuId) return categories || [];
    return (categories || []).filter((c: any) => String(c.menu_id || "") === String(formMenuId));
  }, [categories, ordersMultipleMenusEnabled, formMenuId]);

  const defaultMenuId = useMemo(
    () => orderMenus.find((m) => m.is_default)?.id || orderMenus[0]?.id,
    [orderMenus]
  );
  useEffect(() => {
    if (ordersMultipleMenusEnabled && defaultMenuId && !selectedMenuId) setSelectedMenuId(defaultMenuId);
  }, [ordersMultipleMenusEnabled, defaultMenuId, selectedMenuId]);

  const categoryIdsForSelectedMenu = useMemo(
    () => new Set(categoriesForSelectedMenu.map((c: any) => String(c.id))),
    [categoriesForSelectedMenu]
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const rows = (services || []) as any[];
    return rows.filter((s) => {
      if (ordersMultipleMenusEnabled && selectedMenuId) {
        const inMenu = !s?.category_id || categoryIdsForSelectedMenu.has(String(s.category_id));
        if (!inMenu) return false;
      }
      const name = String(s?.name || "").toLowerCase();
      const matchesSearch = !q || name.includes(q);
      const cid = s?.category_id ? String(s.category_id) : "none";
      const matchesCategory =
        categoryFilter === "all" ? true : categoryFilter === "none" ? cid === "none" : cid === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [services, searchQuery, categoryFilter, ordersMultipleMenusEnabled, selectedMenuId, categoryIdsForSelectedMenu]);

  const resetCategoryForm = () => {
    setCatEditingId(null);
    setCatName("");
    setCatPosition("1");
    setCatActive(true);
    setCatAllowMultiple(false);
    setCatMenuId(selectedMenuId || "");
  };

  const resetForm = () => {
    setEditingId(null);
    setFormName("");
    setFormPrice("");
    setFormDescription("");
    setFormActive(true);
    setFormCategoryId("none");
    setFormMenuId(selectedMenuId || "");
    setMedia([]);
    setFormAddons([]);
    setAddonsLoading(false);
  };

  const openCategories = () => {
    resetCategoryForm();
    setCatMenuId(selectedMenuId || "");
    setIsCategoriesOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = async (row: any) => {
    resetForm();
    setEditingId(String(row.id));
    setFormName(String(row.name || ""));
    setFormPrice(row.price_amount != null ? String(row.price_amount) : "");
    setFormDescription(String(row.description || ""));
    setFormActive(Boolean(row.active));
    setFormCategoryId(row.category_id ? String(row.category_id) : "none");

    // Pre-fill menu from the item's category
    if (row.category_id) {
      const cat = categoryById.get(String(row.category_id));
      setFormMenuId(cat ? String((cat as any).menu_id || selectedMenuId || "") : (selectedMenuId || ""));
    } else {
      setFormMenuId(selectedMenuId || "");
    }

    // Load existing media
    const mediaItems = (row as any)?.media_items;
    if (Array.isArray(mediaItems) && mediaItems.length > 0) {
      setMedia(
        mediaItems
          .map((it: any) => {
            const url = String(it?.url || "").trim();
            const t = String(it?.type || "").toLowerCase();
            if (!url) return null;
            return { url, type: t === "video" ? "video" : "image" } as any;
          })
          .filter(Boolean),
      );
    } else if (row.image_url) {
      // Backward compatible: single primary image
      setMedia([{ url: String(row.image_url), type: "image" }]);
    }

    // Load add-ons
    try {
      setAddonsLoading(true);
      const { data, error } = await supabase
        .from("service_addons" as any)
        .select("id,name,price_amount,max_quantity")
        .eq("service_id", String(row.id))
        .order("created_at", { ascending: true });
      if (error) throw error;
      setFormAddons(
        (data || []).map((a: any) => ({
          id: String(a.id),
          name: String(a.name || ""),
          price: String(a.price_amount ?? 0),
          maxQuantity: String(Math.max(1, Number(a.max_quantity ?? 1))),
        })),
      );
    } catch {
      setFormAddons([]);
    } finally {
      setAddonsLoading(false);
    }

    setOpen(true);
  };

  const handlePresetClick = (preset: PresetItem) => {
    setFormName(preset.name);
    setFormDescription(preset.description);
    setFormPrice(String(preset.price));
    setFormActive(true);
    setOpen(true);
  };

  const saveServiceAddons = async (serviceId: string) => {
    if (!business?.id) return;

    const cleaned = (formAddons || [])
      .map((a) => ({
        id: a.id ? String(a.id) : undefined,
        name: String(a.name || "").trim(),
        price_amount: parseFloat(String(a.price || "0")) || 0,
        max_quantity: Math.max(1, parseInt(String(a.maxQuantity || "1"), 10) || 1),
      }))
      .filter((a) => a.name.length > 0);

    // Guard: unique names (case-insensitive) to avoid confusion in the UI.
    const countsByLower = new Map<string, number>();
    for (const a of cleaned) {
      const k = a.name.toLowerCase();
      countsByLower.set(k, (countsByLower.get(k) || 0) + 1);
    }
    const dupes = Array.from(countsByLower.entries())
      .filter(([, n]) => n > 1)
      .map(([k]) => k);
    if (dupes.length > 0) {
      toast({
        title: "Duplicate add-on names",
        description: `Each add-on name must be unique. Please rename duplicates: ${dupes.join(", ")}.`,
        variant: "destructive",
      });
      throw new Error("Duplicate add-on names");
    }

    // Fetch existing
    const { data: existing, error: exErr } = await supabase
      .from("service_addons" as any)
      .select("id,name,price_amount,max_quantity")
      .eq("service_id", serviceId);
    if (exErr) throw exErr;
    const existingRows = (existing || []) as any[];

    // Prevent deleting referenced add-ons
    const existingIds = existingRows.map((r) => String(r.id));
    const referenced = new Set<string>();
    if (existingIds.length > 0) {
      try {
        const { data: ap } = await supabase
          .from("appointment_addons" as any)
          .select("addon_id")
          .in("addon_id", existingIds);
        (ap || []).forEach((r: any) => referenced.add(String(r.addon_id)));
      } catch {
        // ignore
      }
      try {
        const { data: fo } = await (supabase as any)
          .from("food_order_item_addons")
          .select("addon_id")
          .in("addon_id", existingIds);
        (fo || []).forEach((r: any) => referenced.add(String(r.addon_id)));
      } catch {
        // ignore (table may not exist yet)
      }
    }

    const cleanedIds = new Set(cleaned.filter((a) => !!a.id).map((a) => String(a.id)));
    const toDelete = existingIds.filter((id) => !cleanedIds.has(id) && !referenced.has(id));
    if (toDelete.length > 0) {
      const { error: delErr } = await (supabase as any)
        .from("service_addons")
        .delete()
        .in("id", toDelete);
      if (delErr) {
        // Best-effort: don't block saving the menu item if some deletes fail.
        console.warn("[MenuItemsManager] Some add-ons couldn't be deleted:", delErr);
      }
    }

    // Update existing by id
    const byId = new Map<string, { price_amount: number; max_quantity: number; name: string }>();
    cleaned
      .filter((a) => !!a.id)
      .forEach((a) => byId.set(String(a.id), { name: a.name, price_amount: a.price_amount, max_quantity: a.max_quantity }));

    const updates = existingRows
      .map((ex) => {
        const id = String(ex.id);
        const next = byId.get(id);
        if (!next) return null;
        const curPrice = Number(ex.price_amount ?? 0);
        const curMax = Math.max(1, Number(ex.max_quantity ?? 1));
        const curName = String(ex.name || "");
        if (curPrice === next.price_amount && curMax === next.max_quantity && curName === next.name) return null;
        return { id, ...next };
      })
      .filter(Boolean) as Array<{ id: string; name: string; price_amount: number; max_quantity: number }>;

    if (updates.length > 0) {
      await Promise.all(
        updates.map((u) =>
          (supabase as any)
            .from("service_addons")
            .update({ name: u.name, price_amount: u.price_amount, max_quantity: u.max_quantity })
            .eq("id", u.id),
        ),
      );
    }

    // Insert new
    const toInsert = cleaned.filter((a) => !a.id);
    if (toInsert.length > 0) {
      const { error: insErr } = await (supabase as any).from("service_addons").insert(
        toInsert.map((a) => ({
          business_id: business.id,
          service_id: serviceId,
          name: a.name,
          price_amount: a.price_amount,
          max_quantity: a.max_quantity,
          currency: currency,
          active: true,
        })),
      );
      if (insErr) throw insErr;
    }
  };

  const save = async () => {
    if (!business?.id) return;
    const name = formName.trim();
    if (!name) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    const price = Number(formPrice || 0);
    if (!Number.isFinite(price) || price < 0) {
      toast({ title: "Enter a valid price", variant: "destructive" });
      return;
    }

    // Use `services` table, but set duration/buffers to zero for food items.
    const payload: any = {
      business_id: business.id,
      name,
      description: formDescription.trim() || null,
      price_amount: price,
      currency: currency,
      active: formActive,
      category_id: formCategoryId === "none" ? null : formCategoryId,
      duration_minutes: 0,
      buffer_before_minutes: 0,
      buffer_after_minutes: 0,
      requires_deposit: false,
      deposit_amount: null,
      updated_at: new Date().toISOString(),
    };

    try {
      let serviceId = editingId ? String(editingId) : "";
      if (editingId) {
        const { error } = await (supabase as any).from("services").update(payload).eq("id", editingId);
        if (error) throw error;
      } else {
        payload.created_at = new Date().toISOString();
        const { data, error } = await (supabase as any)
          .from("services")
          .insert(payload)
          .select("id")
          .maybeSingle();
        if (error) throw error;
        serviceId = String(data?.id || "");
      }

      // Save add-ons (best effort, but surface errors)
      if (serviceId) {
        await saveServiceAddons(serviceId);
      }

      toast({ title: editingId ? "Menu item updated" : "Menu item created" });
      setOpen(false);
      resetForm();
      await refetch?.();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const archive = async (id: string) => {
    try {
      const { error } = await (supabase as any).from("services").update({ active: false }).eq("id", id);
      if (error) throw error;
      toast({ title: "Archived" });
      await refetch?.();
    } catch (e: any) {
      toast({ title: "Archive failed", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const persistMedia = async (next: Array<{ url: string; type: "image" | "video"; path?: string }>) => {
    if (!editingId) return;
    const mediaItems = (next || [])
      .map((m) => ({ url: String(m.url || "").trim(), type: m.type === "video" ? "video" : "image" }))
      .filter((m) => !!m.url);
    const primaryImage = mediaItems.find((m) => m.type === "image")?.url || null;
    try {
      await (supabase as any)
        .from("services")
        .update({
          media_items: mediaItems.length ? mediaItems : null,
          image_url: primaryImage,
        })
        .eq("id", editingId);
      await refetch?.();
    } catch {
      // ignore (best effort)
    }
  };

  const uploadMedia = async (files: FileList | null) => {
    if (!files || !files.length || !business?.id) return;

    // Requires a service row id to attach media
    if (!editingId) {
      toast({ title: "Save the item first", description: "Create the menu item before uploading photos/videos.", variant: "destructive" });
      return;
    }

    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) continue;

      const ext = file.name.split(".").pop() || (isVideo ? "mp4" : "jpg");
      // NOTE: bucket is already named "service-media" so don't prefix the object path with "service-media/".
      const path = `${business.id}/${editingId}/${Date.now()}-${Math.random().toString(16).slice(2)}.${ext}`;

      const { error: upErr } = await supabase.storage.from("service-media").upload(path, file, {
        cacheControl: "3600",
        upsert: false,
      });
      if (upErr) {
        toast({ title: "Upload failed", description: upErr.message, variant: "destructive" });
        continue;
      }

      const { data: pub } = supabase.storage.from("service-media").getPublicUrl(path);
      const url = pub?.publicUrl ? String(pub.publicUrl) : "";
      if (!url) continue;

      const next = [...(media || []), { url, type: isVideo ? "video" : "image", path }];
      setMedia(next);
      await persistMedia(next);
    }
  };

  const removeMedia = async (idx: number) => {
    const m = media[idx];
    if (!m) return;
    const next = media.filter((_, i) => i !== idx);
    setMedia(next);
    try {
      if (m.path) {
        await supabase.storage.from("service-media").remove([m.path]);
      }
    } catch {
      // ignore
    }
    await persistMedia(next);
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Menu Items"
        description="Add meals and drinks customers can order"
        action={
          <div className="flex flex-wrap items-center gap-2">
            {ordersMultipleMenusEnabled ? (
              <>
                <Select
                  value={selectedMenuId || ""}
                  onValueChange={(v) => setSelectedMenuId(v || "")}
                  disabled={menusLoading}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={menusLoading ? "Loading..." : "Select menu"} />
                  </SelectTrigger>
                  <SelectContent>
                    {orderMenus.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                        {m.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="pill" onClick={() => { setMenuFormName(""); setMenuEditingId(null); setIsMenusOpen(true); }}>
                  Manage menus
                </Button>
              </>
            ) : null}
            <Button variant="outline" size="pill" onClick={openCategories}>
              Categories
            </Button>
            <Button onClick={openCreate} size="pill">
              <Plus className="mr-2 h-4 w-4" />
              Add menu item
            </Button>
          </div>
        }
      />

      {/* Preset catalog */}
      <div className="mx-auto w-full min-w-0 max-w-7xl px-2 py-4 sm:px-4">
        <Collapsible open={presetsOpen} onOpenChange={setPresetsOpen}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-sm text-primary hover:bg-muted/50 transition-colors">
              <span>Quick add from menu catalog</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${presetsOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3">
            <div className="rounded-lg border border-border bg-background p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div className="text-sm font-medium text-foreground">Choose a category</div>
                <Select value={selectedPresetCategory} onValueChange={setSelectedPresetCategory}>
                  <SelectTrigger className="w-full sm:w-[260px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {menuCatalog.map((c) => (
                      <SelectItem key={c.name} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-2">
                {selectedPresetItems.map((it) => (
                  <button
                    key={it.name}
                    onClick={() => handlePresetClick(it)}
                    className="text-left p-3 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                  >
                    <p className="font-medium text-foreground text-sm">{it.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Info banner */}
        <div className="mt-4 mb-6 flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/20 px-4 py-3 text-sm text-primary">
          <span>
            Items you add here will appear on your public food ordering page.
          </span>
        </div>

        {/* Search + Category filter */}
        <div className="mb-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter} disabled={categoriesLoading}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder={categoriesLoading ? "Loading..." : "Category"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="none">Uncategorized</SelectItem>
              {(ordersMultipleMenusEnabled ? categoriesForSelectedMenu : (categories || []))
                .filter((c: any) => c.active)
                .map((c: any) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
            {filtered.map((row: any) => {
              const mediaItems = Array.isArray(row?.media_items) ? row.media_items : [];
              const primaryMedia = mediaItems.length > 0
                ? { url: String(mediaItems[0]?.url || "").trim(), type: String(mediaItems[0]?.type || "image").toLowerCase() }
                : row.image_url
                  ? { url: String(row.image_url), type: "image" as const }
                  : null;
              const isVideo = primaryMedia?.type === "video";
              return (
              <div key={row.id} className="min-w-0 overflow-hidden bg-card rounded-xl border border-border shadow-soft p-5">
                <div className="flex min-w-0 items-start justify-between gap-3 mb-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {primaryMedia?.url ? (
                      isVideo ? (
                        <video
                          src={primaryMedia.url}
                          className="h-12 w-12 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={primaryMedia.url}
                          alt={String(row.name || "Menu item")}
                          className="h-12 w-12 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="h-12 w-12 rounded-lg border border-border bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                        {String(row.name || "M").slice(0, 1).toUpperCase()}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-foreground">{row.name}</h3>
                      {row.category_id ? (
                        <div className="mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {String(categoryById.get(String(row.category_id))?.name || "Category")}
                          </Badge>
                        </div>
                      ) : null}
                      <p className="mt-1 line-clamp-2 break-words text-sm text-muted-foreground">{row.description}</p>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(row)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => archive(String(row.id))}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-medium text-foreground">
                      {formatPrice(row.price_amount, row.currency || currency)}
                    </span>
                    {!row.active ? <Badge variant="secondary" className="text-xs">Archived</Badge> : null}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <EmptyState
            title="No menu items yet"
            description="Add your first meal or drink so customers can start ordering."
            actionLabel="Add menu item"
            onAction={openCreate}
          />
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit menu item" : "Add menu item"}</DialogTitle>
            <DialogDescription>
              Create meals and drinks customers can order from your public page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Menu selector — only shown when multiple menus exist */}
            {ordersMultipleMenusEnabled && orderMenus.length > 1 && (
              <div className="space-y-2">
                <Label>Menu</Label>
                <Select
                  value={formMenuId}
                  onValueChange={(v) => {
                    setFormMenuId(v);
                    setFormCategoryId("none");
                  }}
                  disabled={menusLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={menusLoading ? "Loading..." : "Select a menu"} />
                  </SelectTrigger>
                  <SelectContent>
                    {orderMenus.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}{m.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Item name</Label>
              <Input id="name" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Jollof Rice" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Price ({currency})</Label>
              <Input
                id="price"
                type="number"
                placeholder="e.g., 35"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe the item (ingredients, portion size, etc.)"
                rows={3}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Category (optional)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    openCategories();
                  }}
                >
                  Manage
                </Button>
              </div>
              <Select value={formCategoryId} onValueChange={setFormCategoryId} disabled={categoriesLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select a category"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categoriesForForm.filter((c: any) => c.active).map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Categories help customers filter your menu.</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <div className="text-sm font-medium text-foreground">Active</div>
                <div className="text-xs text-muted-foreground">Turn off to hide from the public menu</div>
              </div>
              <Switch checked={formActive} onCheckedChange={(v) => setFormActive(Boolean(v))} />
            </div>

            {/* Add-ons */}
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm">Add-ons (optional)</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Extras customers can add to this item (priced per item and multiplied by quantity).
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormAddons((prev) => [
                      ...(prev || []),
                      { name: "", price: "0", maxQuantity: "1" },
                    ])
                  }
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add add-on
                </Button>
              </div>

              {addonsLoading ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading add-ons...
                </div>
              ) : formAddons.length === 0 ? (
                <div className="text-xs text-muted-foreground">No add-ons yet.</div>
              ) : (
                <div className="space-y-2">
                  {formAddons.map((a, idx) => (
                    <div key={a.id || idx} className="rounded-lg border border-border bg-background p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Name</Label>
                            <Input
                              value={a.name}
                              onChange={(e) =>
                                setFormAddons((prev) => {
                                  const next = [...(prev || [])];
                                  next[idx] = { ...next[idx], name: e.target.value };
                                  return next;
                                })
                              }
                              placeholder="e.g., Extra meat"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Price ({currency})</Label>
                            <Input
                              type="number"
                              value={a.price}
                              onChange={(e) =>
                                setFormAddons((prev) => {
                                  const next = [...(prev || [])];
                                  next[idx] = { ...next[idx], price: e.target.value };
                                  return next;
                                })
                              }
                              placeholder="0"
                              min={0}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Max quantity</Label>
                            <Input
                              type="number"
                              value={a.maxQuantity}
                              onChange={(e) =>
                                setFormAddons((prev) => {
                                  const next = [...(prev || [])];
                                  next[idx] = { ...next[idx], maxQuantity: e.target.value };
                                  return next;
                                })
                              }
                              placeholder="1"
                              min={1}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            setFormAddons((prev) => {
                              const next = [...(prev || [])];
                              next.splice(idx, 1);
                              return next;
                            })
                          }
                          aria-label="Remove add-on"
                          title="Remove"
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Media */}
            <div className="space-y-2">
              <Label>Photos & Videos</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!editingId}
                >
                  <Image className="h-4 w-4 mr-2" />
                  Upload
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadMedia(e.target.files)}
                />
                {!editingId ? (
                  <p className="text-xs text-muted-foreground self-center">
                    Save the item first to upload media.
                  </p>
                ) : null}
              </div>

              {media.length ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {media.map((m, idx) => (
                    <div key={m.url} className="relative rounded-lg border border-border overflow-hidden bg-muted">
                      {m.type === "video" ? (
                        <video
                          src={m.url}
                          className="aspect-square object-cover w-full"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img src={m.url} alt="" className="aspect-square object-cover w-full" />
                      )}
                      <button
                        type="button"
                        className="absolute top-1 right-1 rounded-full bg-background/80 border border-border p-1"
                        onClick={() => removeMedia(idx)}
                        aria-label="Remove media"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Add media to make your menu look great.</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={save}>
              {editingId ? "Save changes" : "Create item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories dialog */}
      <Dialog
        open={isCategoriesOpen}
        onOpenChange={(v) => {
          setIsCategoriesOpen(v);
          if (!v) resetCategoryForm();
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Menu Categories</DialogTitle>
            <DialogDescription>Organize your menu so customers can browse faster.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Create / Edit form */}
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-4">
              {ordersMultipleMenusEnabled ? (
                <div className="space-y-2">
                  <Label>Menu</Label>
                  <Select value={catMenuId} onValueChange={setCatMenuId} disabled={menusLoading}>
                    <SelectTrigger>
                      <SelectValue placeholder={menusLoading ? "Loading..." : "Select menu"} />
                    </SelectTrigger>
                    <SelectContent>
                      {orderMenus.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category name</Label>
                  <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="e.g., Rice & Bowls" />
                </div>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input value={catPosition} onChange={(e) => setCatPosition(e.target.value)} type="number" min={1} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
                <div>
                  <div className="text-sm font-medium text-foreground">Active</div>
                  <div className="text-xs text-muted-foreground">Inactive categories won’t show in filters.</div>
                </div>
                <Switch checked={catActive} onCheckedChange={(v) => setCatActive(Boolean(v))} />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
                <div>
                  <div className="text-sm font-medium text-foreground">Allow multi-select</div>
                  <div className="text-xs text-muted-foreground">
                    Let customers pick multiple services from this category (e.g., extras).
                  </div>
                </div>
                <Switch checked={catAllowMultiple} onCheckedChange={(v) => setCatAllowMultiple(Boolean(v))} />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={async () => {
                    const name = catName.trim();
                    if (!name) {
                      toast({ title: "Name is required", variant: "destructive" });
                      return;
                    }
                    if (ordersMultipleMenusEnabled && !catMenuId) {
                      toast({ title: "Select a menu", variant: "destructive" });
                      return;
                    }
                    const pos = Math.max(1, Number(catPosition || 1));
                    const payload: any = {
                      name,
                      position: pos,
                      active: catActive,
                      allow_multiple_services: catAllowMultiple,
                    };
                    if (ordersMultipleMenusEnabled && catMenuId) payload.menu_id = catMenuId;
                    try {
                      if (catEditingId) {
                        await updateCategory(catEditingId, payload);
                      } else {
                        await createCategory(payload);
                      }
                      resetCategoryForm();
                    } catch (e: any) {
                      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" });
                    }
                  }}
                >
                  {catEditingId ? "Save category" : "Add category"}
                </Button>
                {catEditingId ? (
                  <Button type="button" variant="outline" onClick={resetCategoryForm}>
                    Cancel edit
                  </Button>
                ) : null}
              </div>
            </div>

            {/* List */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Your categories</div>
              {(categories || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No categories yet.</div>
              ) : (
                <div className="space-y-2">
                  {(ordersMultipleMenusEnabled ? categoriesForSelectedMenu : (categories || [])).map((c: any) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-foreground truncate">{c.name}</div>
                          {!c.active ? <Badge variant="secondary" className="text-xs">Inactive</Badge> : null}
                        </div>
                        <div className="text-xs text-muted-foreground">Position: {Number(c.position || 1)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCatEditingId(String(c.id));
                            setCatName(String(c.name || ""));
                            setCatPosition(String(c.position ?? 1));
                            setCatActive(Boolean(c.active));
                            setCatAllowMultiple(Boolean((c as any)?.allow_multiple_services));
                            setCatMenuId(String((c as any)?.menu_id || ""));
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await updateCategory(String(c.id), { active: !c.active } as any);
                              if (categoryFilter === String(c.id) && c.active) setCategoryFilter("all");
                            } catch (e: any) {
                              toast({
                                title: "Update failed",
                                description: e?.message || "Try again.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          {c.active ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={async () => {
                            try {
                              await deleteCategory(String(c.id));
                              if (formCategoryId === String(c.id)) setFormCategoryId("none");
                              if (categoryFilter === String(c.id)) setCategoryFilter("all");
                            } catch (e: any) {
                              toast({
                                title: "Delete failed",
                                description: e?.message || "Try again.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsCategoriesOpen(false)} className="w-full sm:w-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Menus dialog */}
      <Dialog open={isMenusOpen} onOpenChange={(v) => { setIsMenusOpen(v); if (!v) { setMenuFormName(""); setMenuEditingId(null); } }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Menus</DialogTitle>
            <DialogDescription>Create menus (e.g. Regular, Special occasions). Each menu has its own categories and items.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
              <Input
                placeholder="Menu name (e.g. Special occasions)"
                value={menuFormName}
                onChange={(e) => setMenuFormName(e.target.value)}
                className="min-w-0 flex-1"
              />
              <div className="flex flex-shrink-0 gap-2">
                <Button
                  className="flex-1 sm:flex-none"
                  onClick={async () => {
                    const name = menuFormName.trim();
                    if (!name) {
                      toast({ title: "Name is required", variant: "destructive" });
                      return;
                    }
                    try {
                      if (menuEditingId) {
                        await updateMenu(menuEditingId, { name });
                        setMenuEditingId(null);
                        setMenuFormName("");
                      } else {
                        await createMenu({ name });
                        setMenuFormName("");
                      }
                    } catch (e: any) {
                      toast({ title: "Failed", description: e?.message || "Try again.", variant: "destructive" });
                    }
                  }}
                >
                  {menuEditingId ? "Update" : "Add menu"}
                </Button>
                {menuEditingId ? (
                  <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => { setMenuEditingId(null); setMenuFormName(""); }}>
                    Cancel
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="space-y-2">
              {orderMenus.length === 0 ? (
                <p className="text-sm text-muted-foreground">No menus yet. Add one above.</p>
              ) : (
                orderMenus.map((m) => (
                  <div key={m.id} className="flex flex-col gap-3 rounded-lg border border-border bg-background p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="min-w-0">
                      <span className="font-medium text-foreground">{m.name}</span>
                      <span className="text-muted-foreground text-sm ml-1 sm:ml-2">/{m.slug}</span>
                      {m.is_default ? <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">Default</Badge> : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => { setMenuFormName(m.name); setMenuEditingId(m.id); }}>
                        Edit
                      </Button>
                      {!m.is_default ? (
                        <Button size="sm" variant="outline" onClick={() => setDefaultMenu(m.id)}>Set default</Button>
                      ) : null}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                        onClick={async () => {
                          try {
                            await deleteMenu(m.id);
                            if (selectedMenuId === m.id) setSelectedMenuId(defaultMenuId || "");
                          } catch (e: any) {
                            toast({ title: "Delete failed", description: e?.message || "Try again.", variant: "destructive" });
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setIsMenusOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

